// ============================================================================
// JARVISTRAVEL - PICKING UP AN UNFINISHED SIGN-IN (JAR-2492)
//
// Clerk keeps an unfinished sign-in or sign-up with its client across a reload
// or a relaunch, and a code it has sent stays good until it expires. An iPhone
// often relaunches a Home Screen app when the person comes back from Mail or
// their authenticator. So the auth screens ask here which step Clerk is waiting
// on and start there, instead of at an empty form that sends another code.
// ============================================================================

import type { SignInResource, SignUpResource } from '@clerk/shared/types';

type Verification = { status: string | null; strategy: string | null; expireAt: Date | null } | null | undefined;

/** True when Clerk sent a code of this kind for the step, and it hasn't expired or been used. */
export function codeStillGood(verification: Verification, strategy: string): boolean {
  return (
    verification?.strategy === strategy &&
    verification.status === 'unverified' &&
    verification.expireAt !== null &&
    verification.expireAt.getTime() > Date.now()
  );
}

/**
 * True when a second-factor code Clerk already sent — the Device Trust email or
 * the texted code of a phone-only attempt (JAR-2872) — is still good, so the
 * interrupted attempt resumes without sending another.
 */
export function secondFactorCodeStillGood(signIn: SignInResource | null | undefined): boolean {
  return (
    codeStillGood(signIn?.secondFactorVerification, 'email_code') ||
    codeStillGood(signIn?.secondFactorVerification, 'phone_code')
  );
}

/** True when a password reset is waiting on the emailed code, and that code still works. */
export function resetAwaitingCode(signIn: SignInResource | null | undefined): boolean {
  return signIn?.status === 'needs_first_factor' && codeStillGood(signIn.firstFactorVerification, 'reset_password_email_code');
}

/**
 * True when a password reset has had its code accepted and waits on the new
 * password. A temporary password reaches the same status by strategy
 * `password`; that one is the sign-in form's.
 */
export function resetAwaitingNewPassword(signIn: SignInResource | null | undefined): boolean {
  return (
    signIn?.status === 'needs_new_password' &&
    signIn.firstFactorVerification?.strategy === 'reset_password_email_code' &&
    signIn.firstFactorVerification.status === 'verified'
  );
}

/** True when a sign-up is waiting on the emailed code, and that code still works. */
export function signUpAwaitingCode(signUp: SignUpResource | null | undefined): boolean {
  return signUp?.status === 'missing_requirements' && codeStillGood(signUp.verifications?.emailAddress, 'email_code');
}

// Once per page load. Picking up an interrupted flow can hand the person on to
// another screen, and Back from that screen must not hand them on again.
let resumed = false;

/** True the first time per page load: the screen that gets it picks the flow up. */
export function claimResume(): boolean {
  if (resumed) return false;
  resumed = true;
  return true;
}

export function __resetResume(): void {
  resumed = false;
}
